import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Joyride, { EVENTS, STATUS } from 'react-joyride';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { useUserStore } from 'Store/userStore';

const AppGuide = ({ children }) => {
    const user = useUserStore((state) => state.user);
    const [runTour, setRunTour] = useState(false);
    const [tourVisible, setTourVisible] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const history = useHistory();

    useEffect(() => {
        const hasVisited = localStorage.getItem('hasVisited');
        if (!hasVisited || hasVisited === 'false') {
            setTourVisible(true);
        }
    }, []);

    const handleStartTour = () => {
        history.push('/');
        setTimeout(() => {
            setRunTour(true);
            setStepIndex(0);
        }, 300);
    };

    const handleJoyrideCallback = useCallback(
        (data) => {
            const { status, index, type } = data;
            const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

            if (finishedStatuses.includes(status)) {
                localStorage.setItem('hasVisited', 'true');
                setRunTour(false);
                setTourVisible(false);
                return;
            }

            if (type === EVENTS.STEP_AFTER) {
                if (index === 4) {
                    setRunTour(false);
                    history.push('/apps');
                    const observer = new MutationObserver(() => {
                        const el = document.querySelector('.guide_apps-container');
                        if (el) {
                            observer.disconnect();
                            setTimeout(() => {
                                setStepIndex(5);
                                setRunTour(true);
                            }, 200);
                        }
                    });

                    observer.observe(document.body, {
                        childList: true,
                        subtree: true,
                    });

                    return;
                }

                if (index === 6) {
                    setRunTour(false);
                    history.push('/profile/password');

                    const wait = setInterval(() => {
                        const el = document.querySelector('.guide_profile-changepwd');
                        if (el) {
                            clearInterval(wait);
                            setTimeout(() => {
                                setStepIndex(7);
                                setRunTour(true);
                            }, 300);
                        }
                    }, 300);
                    return;
                }

                setStepIndex((prev) => prev + 1);
            }

            if (type === EVENTS.TARGET_NOT_FOUND) {
                console.warn('Target not found for step:', index);
            }
        },
        [history],
    );

    const steps = [
        {
            target: '.guide_navbar-logo',
            content:
                "Ceci est le logo de l'application. Cliquez dessus à tout moment pour revenir à la page d’accueil.",
            disableBeacon: true,
        },
        {
            target: '.guide_navbar-home-item',
            content:
                'Ce bouton vous permet d’accéder à la page d’accueil, où sont regroupées les notifications importantes et générales.',
        },
        {
            target: '.guide_home-alerts',
            content:
                "Cette section affiche les alertes critiques émises par l'administration ou le service informatique.",
        },
        {
            target: '.guide_home-notifications',
            content:
                'Vous trouverez ici les notifications générales provenant de différents systèmes tels que SITEC, GVAO, GART, etc.',
        },
        {
            target: '.guide_navbar-apps-item',
            content: 'Accédez ici aux applications internes disponibles via la plateforme CLIAM.',
        },
        {
            target: '.guide_apps-container',
            content: 'Voici la liste des applications disponibles. Cliquez sur une carte pour y accéder rapidement.',
        },
        {
            target: '.guide_account-section',
            content:
                'Cliquez ici pour accéder aux options de compte, comme le profil ou la déconnexion. Ensuite, cliquez sur "Profil".',
        },
        {
            target: '.guide_profile-changepwd',
            content:
                "Ici, vous pouvez modifier votre mot de passe en saisissant l’ancien, le nouveau, puis en le confirmant. Cliquez ensuite sur 'Valider'.",
        },
    ];

    return (
        <>
            {user && tourVisible && !runTour && (
                <div
                    className="position-fixed bottom-0 end-0 p-3"
                    style={{ zIndex: 11000, bottom: '5px', right: '5px' }}
                >
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="start-tour-tooltip">Lancer le guide</Tooltip>}
                    >
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleStartTour}
                            className="shadow-sm rounded-pill px-4 py-2 fw-bold"
                        >
                            🚀 Faire un tour
                        </Button>
                    </OverlayTrigger>
                </div>
            )}

            <Joyride
                steps={steps}
                run={runTour}
                stepIndex={stepIndex}
                continuous
                showSkipButton
                hideBackButton
                disableCloseOnEsc
                disableOverlayClose
                scrollToFirstStep
                showProgress={false}
                locale={{
                    back: 'Précédent',
                    close: 'Fermer',
                    last: 'Terminer',
                    next: 'Suivant',
                    skip: 'Passer',
                }}
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        zIndex: 10000,
                        primaryColor: '#007bff',
                        backgroundColor: '#fdfdfd',
                        textColor: '#333',
                        arrowColor: '#fdfdfd',
                        overlayColor: 'rgba(0, 0, 0, 0.5)',
                    },
                    tooltipTitle: {
                        fontSize: 16,
                        fontWeight: 'bold',
                    },
                    buttonNext: {
                        backgroundColor: '#7ca3cd',
                        borderRadius: '5px',
                    },
                    buttonBack: {
                        color: '#6c757d',
                    },
                }}
            />
            {children}
        </>
    );
};

export default AppGuide;
